import SesamyNivoTheme from "@/components/Nivo/SesamyNivoTheme";
import Tooltip from "@/components/Nivo/Tooltip";
import Column from "@/components/Ui/Column";
import Row from "@/components/Ui/Row";
import { gradient } from "@/constants/color-gradient";
import { DynamicObject } from "@/types/common";
import { StatsApiGraphConfig } from "@/types/graph-api";
import { ceilToNearest } from "@/utils/calculations";
import { isValidDate } from "@/utils/dates";
import {
  longDateTime,
  numberFormat,
  shortDateFormat,
} from "@/utils/numberFormat";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";

// Updated to find both min and max Y values
const findYRange = (data: DynamicObject[]) => {
  let biggestY = -Infinity;
  let smallestY = Infinity;

  for (const obj of data) {
    for (const point of obj.data) {
      if (point.y > biggestY) {
        biggestY = point.y;
      }
      if (point.y < smallestY) {
        smallestY = point.y;
      }
    }
  }

  return { min: smallestY, max: biggestY };
};

// Function to floor to nearest specified value
const floorToNearest = (num: number, nearest: number = 10) => {
  return Math.floor(num / nearest) * nearest;
};

const DEFAULT_TIME_FORMAT = "%Y-%m-%d";

const LineChartTheme = structuredClone(SesamyNivoTheme);
(LineChartTheme.axis.ticks.text as any).textAnchor = "start";

const ResponsiveLine = dynamic(
  () => import("@nivo/line").then(({ ResponsiveLine }) => ResponsiveLine),
  { ssr: false },
);

const Line = ({
  data,
  isLegacyGraph = false,
  singleDate = false,
  isCurrency = false,
  graphConfig,
  fullyRendered = () => {},
}: {
  data: {
    id: string;
    data: {
      x: string;
      y: number;
    }[];
  }[];
  isLegacyGraph?: boolean;
  singleDate?: boolean;
  isCurrency?: boolean;
  graphConfig?: StatsApiGraphConfig;
  fullyRendered?: Function;
}) => {
  const [linearGradientDef, setLinearGradientDef] = useState<any>(null);
  const disabledLegendsState = useState<string[]>([]);
  const [disabledLegends] = disabledLegendsState;

  const colors = Object.values(gradient) as string[];

  const coloredData = data.map((point, i) => ({
    ...point,
    color: colors[i % colors.length],
  }));
  const filteredData = coloredData.filter(
    ({ id }) => !disabledLegends.includes(id),
  );

  let correctedData = filteredData;

  singleDate = singleDate || (data.length > 0 && data[0].data.length === 1);

  if (singleDate) {
    correctedData = filteredData.map((data) => ({
      ...data,
      data: [
        { ...data.data[0], x: shortDateFormat(data.data[0].x) },
        data.data[0],
      ],
    }));
  }

  const timeFormat = graphConfig?.timeFormat || DEFAULT_TIME_FORMAT;
  const stacked =
    graphConfig?.stacked !== undefined ? !!graphConfig.stacked : false;
  const defaultToPercentage = !!graphConfig?.defaultToPercentage;

  // Get both min and max Y values
  let yMin, yMax;

  if (defaultToPercentage) {
    yMin = 0;
    yMax = 100;
  } else if (!stacked) {
    const yRange = findYRange(filteredData);
    yMin = yRange.min;
    yMax = yRange.max;
  } else {
    // For stacked charts, we need to calculate the sum at each x point
    const sums: Record<string, number> = {};
    const negSums: Record<string, number> = {};

    filteredData.forEach((serie) => {
      serie.data.forEach((point) => {
        const x = point.x;
        if (point.y >= 0) {
          sums[x] = (sums[x] || 0) + point.y;
        } else {
          negSums[x] = (negSums[x] || 0) + point.y;
        }
      });
    });

    yMax =
      Object.values(sums).length > 0
        ? Math.max(...(Object.values(sums) as number[]))
        : 0;
    yMin =
      Object.values(negSums).length > 0
        ? Math.min(...(Object.values(negSums) as number[]))
        : 0;
  }
  // Ceiling and floor values for better visualization
  const max = yMax <= 0 ? 0 : ceilToNearest(yMax);
  const min = yMin >= 0 ? 0 : floorToNearest(yMin);

  useEffect(() => {
    (async () => {
      const nivoCore = await import("@nivo/core");

      setLinearGradientDef(
        nivoCore.linearGradientDef("gradientA", [
          { offset: 0, color: "inherit" },
          { offset: 100, color: "inherit", opacity: 0 },
        ]),
      );
    })();
  }, []);

  if (!linearGradientDef) return null;

  // Calculate tick values for the y-axis
  const calculateTickValues = () => {
    if (defaultToPercentage) return [0, 25, 50, 75, 100];

    if (min >= 0) {
      return [0, max * 0.25, max * 0.5, max * 0.75, max];
    } else if (max <= 0) {
      return [min, min * 0.75, min * 0.5, min * 0.25, 0];
    } else {
      // Handle case with both positive and negative values
      const range = max - min;
      return [
        min,
        min + range * 0.25,
        min + range * 0.5,
        min + range * 0.75,
        max,
      ];
    }
  };

  return (
    <>
      <div className="-ml-[30px] aspect-[2.5/1] max-h-[300px] w-[calc(100%+30px)]">
        {data.length > 0 && data[0].data.length > 0 && (
          <ResponsiveLine
            layers={[
              "grid",
              "markers",
              "axes",
              "areas",
              "crosshair",
              "lines",
              "points",
              "slices",
              "mesh",
              "legends",
              () => fullyRendered(),
            ]}
            data={correctedData}
            theme={LineChartTheme}
            colors={({ color }) => color}
            enablePoints={false}
            enableArea={true}
            defs={[linearGradientDef]}
            fill={[{ match: "*", id: "gradientA" }]}
            sliceTooltip={(datum) => {
              const {
                slice: { points },
              } = datum;

              const sortedPoints = Array.from(points).sort(
                ({ data: { y: aY } }: any, { data: { y: bY } }: any) => bY - aY,
              );

              const date = sortedPoints[0].data.x as string;
              const xFormatted = sortedPoints[0].data.xFormatted as string;

              return (
                <Tooltip className="animate-in fade-in column-left gap-2">
                  <Column className="gap-2" left>
                    {!isLegacyGraph && (
                      <span className="font-medium">
                        {isValidDate(date)
                          ? xFormatted.split(":").length == 4 // Checks for format "YYYY-MM-DD:hh:ii:ss"
                            ? longDateTime(date)
                            : shortDateFormat(date)
                          : String(sortedPoints[0].data.x)}
                      </span>
                    )}
                    <Column className="w-full gap-1" left>
                      {!isLegacyGraph &&
                        !graphConfig?.hideTotal &&
                        sortedPoints.length > 1 && (
                          <>
                            <Row
                              className="w-full justify-between! gap-10 font-medium"
                              left
                            >
                              <Row className="gap-2" left>
                                <span className="capitalize">Total</span>
                              </Row>
                              {numberFormat(
                                sortedPoints.reduce(
                                  (a, { data: { y } }: any) => a + y,
                                  0,
                                ) / (isCurrency ? 100 : 1),
                                false,
                                isCurrency ? 0 : 2,
                              )}
                            </Row>
                            <hr className="from-border my-2 h-px w-full border-none bg-linear-to-r to-transparent" />
                          </>
                        )}
                      {sortedPoints.map(
                        ({ serieId, serieColor, data: { yFormatted, y } }) => (
                          <Row
                            className="w-full justify-between! gap-10"
                            key={serieId}
                            left
                          >
                            <Row className="gap-2" left>
                              <div
                                className="h-2 w-2 -translate-y-px rounded-full"
                                style={{
                                  backgroundColor: serieColor,
                                }}
                              />
                              <span className="capitalize">{serieId}</span>
                            </Row>
                            <>
                              {defaultToPercentage
                                ? `${yFormatted}%`
                                : numberFormat(
                                    parseFloat(y as string) /
                                      (isCurrency ? 100 : 1),
                                    false,
                                    isCurrency ? 0 : 2,
                                  )}
                            </>
                          </Row>
                        ),
                      )}
                    </Column>
                  </Column>
                </Tooltip>
              );
            }}
            gridYValues={4}
            enableGridX={false}
            enableSlices="x"
            margin={{
              top: defaultToPercentage ? 50 : 10,
              right: defaultToPercentage ? 30 : 40,
              bottom: 25,
              left: 30,
            }}
            xFormat={`time:${timeFormat}`}
            xScale={
              isLegacyGraph || singleDate
                ? { type: "point" }
                : { type: "time", format: timeFormat, useUTC: false }
            }
            yScale={{
              type: "linear",
              min,
              max,
              stacked,
              reverse: false,
            }}
            curve={defaultToPercentage ? "linear" : "monotoneX"}
            yFormat=" >-.2f"
            axisTop={null}
            axisRight={{
              tickValues: calculateTickValues(),
              format: (value) => {
                const isNumber = Math.floor(value) === value;

                if (isNumber) {
                  const number = value / (isCurrency ? 100 : 1);
                  return number >= 1000000 || number <= -1000000
                    ? `${number / 1000000}M`
                    : number >= 1000 || number <= -1000
                      ? `${number / 1000}K`
                      : number;
                }
                return "";
              },
            }}
            axisBottom={
              isLegacyGraph
                ? {}
                : {
                    tickValues: Math.min(
                      filteredData[0]?.data?.length - 1 || 0,
                      6,
                    ),
                    format: (e) =>
                      isValidDate(e) ? shortDateFormat(e, false) : e,
                  }
            }
            axisLeft={null}
            pointSize={10}
            pointColor={{ theme: "background" }}
            pointBorderWidth={2}
            pointBorderColor={{ from: "serieColor" }}
            pointLabelYOffset={-12}
            useMesh={true}
            // Add markers for zero line if we have negative values
            markers={
              min < 0
                ? [
                    {
                      axis: "y",
                      value: 0,
                      lineStyle: {
                        stroke: "#888",
                        strokeWidth: 1,
                        strokeDasharray: "4 4",
                      },
                    },
                  ]
                : []
            }
          />
        )}
      </div>
    </>
  );
};

export default Line;
